import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, DollarSign, Landmark, MessageCircle } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const DEFAULTS = {
  exchange_rate_jmd_per_usd: 155,
  banking_description: "",
  whatsapp_payment_url: "https://wa.me/message/TF3W5QQUVUKZF1",
  payment_instructions: "",
};

export default function AdminSettings() {
  const { user } = useAuth();
  const canEdit = user && ["admin", "super_admin"].includes(user.role);
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setForm({ ...DEFAULTS, ...r.data }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        exchange_rate_jmd_per_usd: Number(form.exchange_rate_jmd_per_usd) || 0,
      };
      if (!payload.exchange_rate_jmd_per_usd || payload.exchange_rate_jmd_per_usd <= 0) {
        toast.error("Exchange rate must be greater than zero");
        setSaving(false);
        return;
      }
      const r = await api.put("/settings", payload);
      setForm({ ...DEFAULTS, ...r.data });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <div data-testid="admin-settings">
      <div className="overline text-zinc-500">Storefront</div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-2">Site settings</h1>
      <p className="text-sm text-zinc-500 mb-8">
        Update the JMD↔USD exchange rate, banking information shown on customer receipts, and the
        WhatsApp credit-card payment link.
      </p>

      <div className="space-y-6 max-w-3xl">
        {/* Currency */}
        <section className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign size={18} style={{ color: "#F74B64" }} />
            <h2 className="font-display font-bold text-lg">Currency</h2>
          </div>
          <p className="text-sm text-zinc-500 mb-4">
            Customers can toggle the storefront between JMD and USD. Orders are always processed
            and stored in JMD; this rate is used purely for display.
          </p>
          <label className="block">
            <span className="text-xs font-semibold text-zinc-600">Exchange rate (1 USD = ? JMD)</span>
            <input
              type="number"
              min="1"
              step="0.01"
              value={form.exchange_rate_jmd_per_usd}
              onChange={update("exchange_rate_jmd_per_usd")}
              disabled={!canEdit}
              className="mt-1 w-full sm:w-60 border border-zinc-300 rounded-lg px-3 py-2 text-sm"
              data-testid="settings-exchange-rate"
            />
          </label>
          <p className="text-xs text-zinc-500 mt-2">
            Example: <code>155</code> means $1 USD ≈ JMD $155. Update whenever the market rate
            shifts so USD prices stay accurate.
          </p>
        </section>

        {/* Banking */}
        <section className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Landmark size={18} style={{ color: "#F74B64" }} />
            <h2 className="font-display font-bold text-lg">Bank transfer details</h2>
          </div>
          <p className="text-sm text-zinc-500 mb-4">
            Shown on the order receipt so customers can pay by direct deposit. Plain text — line
            breaks are preserved exactly as you type them.
          </p>
          <textarea
            rows={6}
            value={form.banking_description}
            onChange={update("banking_description")}
            disabled={!canEdit}
            placeholder={"Bank: NCB\nAccount name: Fruit Blossoms Ltd\nAccount #: 1234567890\nBranch: Half-Way Tree\nUse your order number as the reference."}
            className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm font-mono leading-relaxed"
            data-testid="settings-banking-description"
          />
        </section>

        {/* WhatsApp */}
        <section className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle size={18} style={{ color: "#25D366" }} />
            <h2 className="font-display font-bold text-lg">WhatsApp payment link</h2>
          </div>
          <p className="text-sm text-zinc-500 mb-4">
            Customers who want to pay by credit or debit card click this link from their receipt. Use a
            <code className="px-1">wa.me</code> short link so it opens directly into a WhatsApp chat.
          </p>
          <label className="block">
            <span className="text-xs font-semibold text-zinc-600">WhatsApp URL</span>
            <input
              type="url"
              value={form.whatsapp_payment_url}
              onChange={update("whatsapp_payment_url")}
              disabled={!canEdit}
              placeholder="https://wa.me/message/XXXXXXXX"
              className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm font-mono"
              data-testid="settings-whatsapp-url"
            />
          </label>

          <label className="block mt-4">
            <span className="text-xs font-semibold text-zinc-600">Short headline (above payment options)</span>
            <input
              type="text"
              value={form.payment_instructions}
              onChange={update("payment_instructions")}
              disabled={!canEdit}
              placeholder="Please complete payment within 24 hours to confirm your order."
              className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm"
              data-testid="settings-payment-instructions"
            />
          </label>
        </section>

        <div className="pt-2">
          <button
            onClick={save}
            disabled={!canEdit || saving}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="settings-save-btn"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
